import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button, Stack } from '@optimus-web/core';
import { FormMetaSection, LocalizedLabel } from '@mews-commander/ui';
import {
    AlternativePaymentMethodIntegrationResult,
    AlternativePaymentMethodIntegrations,
} from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { FormSwitchField, LocalizedText, useTimezone } from '@mews/framework';
import { PaymentMethodImageType } from '@mews/business-components';
import { utcToLocalizedDateTime } from '@mews-commander/utils';
import { PaymentMethodIcon } from '../../../../../components/commander-ui/PaymentMethodIcon';
import { AlternativePaymentMethodIntegrationFormValues } from './schema';

type FormContentMeta = Pick<
    AlternativePaymentMethodIntegrationResult,
    'createdUtc' | 'creatorProfileName' | 'updatedUtc' | 'updaterProfileName'
>;

export const AlternativePaymentMethodIntegrationFormContent = ({
    onSubmit,
    meta: { createdUtc, creatorProfileName, updatedUtc, updaterProfileName },
}: {
    onSubmit: () => void;
    meta: FormContentMeta;
}) => {
    const timezone = useTimezone();
    const [isPristine, setPristine] = useState(true);
    const {
        reset,
        formState: { isDirty, isSubmitSuccessful },
        getValues,
    } = useFormContext<AlternativePaymentMethodIntegrationFormValues>();

    useEffect(() => {
        if (isSubmitSuccessful) {
            // resets form dirty state after the submit
            reset(getValues());
            setPristine(true);
        }
    }, [isSubmitSuccessful, reset, getValues]);

    useEffect(() => {
        // dirty state is reset when field is set to default value, pristine is reset after the first change made
        if (isPristine && isDirty) {
            setPristine(false);
        }
    }, [isPristine, isDirty]);

    return (
        <Stack spacing="200" vertical>
            <LocalizedLabel textKey={TextKey.IntegrationEnableAlternativePaymentMethods} as="div" />
            <Stack distribution="stretch" spacing="100">
                <PaymentMethodIcon type={PaymentMethodImageType.SepaDirectDebit} small />
                <FormSwitchField
                    label={<LocalizedText textKey={TextKey.SEPADirectDebitTransactionCost} />}
                    name={AlternativePaymentMethodIntegrations.SepaDirectDebit}
                />
            </Stack>
            <Stack distribution="stretch" spacing="100">
                <PaymentMethodIcon type={PaymentMethodImageType.Ideal} small />
                <FormSwitchField
                    label={<LocalizedText textKey={TextKey.IDealTransactionCost} />}
                    name={AlternativePaymentMethodIntegrations.Ideal}
                />
            </Stack>
            <FormMetaSection
                meta={{
                    createdDateTime: utcToLocalizedDateTime(createdUtc, timezone),
                    creatorName: creatorProfileName,
                    updatedDateTime: utcToLocalizedDateTime(updatedUtc, timezone),
                    updaterName: updaterProfileName,
                }}
            />
            <Stack alignment="end">
                <Button variant="primary" onClick={onSubmit} disabled={isPristine} type="submit">
                    <LocalizedText textKey={TextKey.Save} />
                </Button>
            </Stack>
        </Stack>
    );
};
